import { useMemo, useState } from 'react';
import { perimeterProgress, type SectionKey } from './CreationProgress';
import { fmt } from './domain';
import { StepPerimeter, type PerimeterDraft } from './StepPerimeter';
import type { PlanBuilding, PlanProject, Referential } from './types';
import { derive, type SettingsDraft } from './configDraft';

interface Props {
  project: PlanProject;
  buildings: PlanBuilding[];
  referential: Referential;
  draft: SettingsDraft;
  onDraftChange: (patch: Partial<SettingsDraft>) => void;
}

const ALL_TOUCHED: Record<SectionKey, boolean> = { name: true, perimeter: true, weather: true, kind: true, contract: true };

/** Onglet « Périmètres » : l'étape 1 de l'assistant de création, périmètre en lecture seule. */
export function SettingsPerimeterTab({ project, buildings, referential, draft, onDraftChange }: Props) {
  const [touched, setTouched] = useState<Partial<Record<SectionKey, boolean>>>(ALL_TOUCHED);
  const p = project.perimeter;
  const perimeterDraft: PerimeterDraft = {
    name: draft.name, kind: draft.kind, networkIds: draft.networkIds, contract: draft.contract,
    mode: p.kind === 'drawn' ? 'drawn' : 'communes',
    communes: p.kind === 'communes' ? p.communes : [],
    shape: 'free', points: p.kind === 'drawn' ? p.polygon : [], closed: true, drawing: false,
  };
  const includedIds = useMemo(() => new Set(buildings.map((b) => b.id)), [buildings]);
  const stationId = draft.dju.stationId;
  const progress = perimeterProgress(perimeterDraft, buildings.length, referential.stations.some((s) => s.id === stationId), touched);

  const change = (patch: Partial<PerimeterDraft>) => {
    const next: Partial<SettingsDraft> = {};
    if (patch.name !== undefined) next.name = patch.name;
    if (patch.kind !== undefined) next.kind = patch.kind;
    if (patch.networkIds !== undefined) next.networkIds = patch.networkIds;
    if (patch.contract !== undefined) next.contract = patch.contract;
    if (Object.keys(next).length > 0) onDraftChange(next);
  };
  const chooseStation = (id: string) => {
    const s = referential.stations.find((x) => x.id === id);
    if (!s || s.id === stationId) return;
    const stationDegrees = { dju: s.dju, djclim: s.djclim };
    const next = derive({ ...draft, stationDegrees, dju: { stationId: id, reference: s.dju } });
    onDraftChange({ stationDegrees, ratios: next.ratios, dju: next.dju });
  };

  return (
    <StepPerimeter
      referential={referential}
      draft={perimeterDraft}
      onChange={change}
      included={buildings}
      includedIds={includedIds}
      stationId={stationId}
      onStationSelect={chooseStation}
      progress={progress}
      onTouch={(key) => setTouched((t) => (t[key] ? t : { ...t, [key]: true }))}
      actions={null}
      lockedPerimeter={`Lecture seule : ${fmt(project.buildingCount)} bâtiments copiés d’UrbanMap le ${project.snapshotDate}.`}
    />
  );
}
